import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { isWelcomeComplete } from '../utils/welcomeComplete';
import { remainingUnlockMs, splitCountdown, padUnit } from '../utils/unlockCountdown';
import GenerativeBackground from './GenerativeOrbAnimation';
import './UnlockPage.css';

const UnlockPage = () => {
    const [isPremium, setIsPremium] = useState(null);
    const [userPhoto, setUserPhoto] = useState('');
    const [userName, setUserName] = useState('');
    const [requestStatus, setRequestStatus] = useState('idle'); // idle | sending | sent | error
    const [requestError, setRequestError] = useState('');
    const [requestedAt, setRequestedAt] = useState(null);
    const [remainingMs, setRemainingMs] = useState(0);
    
    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const navigate = useNavigate();

    useEffect(() => {
        if (currentUser) {
            const userRef = ref(db, `Users/${currentUser.uid}`);
            get(userRef).then(snapshot => {
                if (snapshot.exists()) {
                    const userData = snapshot.val();
                    if (!isWelcomeComplete(userData)) {
                        navigate('/welcome');
                        return;
                    }
                    const premiumStatus = userData?.isPremium ?? false;
                    setIsPremium(premiumStatus);
                    setUserPhoto(userData?.userPhoto || '');
                    setUserName(userData?.name || 'User');
                    const existingRequest = userData?.unlockRequest?.requestedAt;
                    if (existingRequest) {
                        setRequestedAt(existingRequest);
                        setRequestStatus('sent');
                    }
                    
                    if (premiumStatus) {
                        navigate('/');
                    }
                } else {
                    navigate('/welcome');
                }
            }).catch(error => {
                console.error('Error fetching user data:', error);
            });
        }
    }, [currentUser, navigate, db]);

    useEffect(() => {
        if (!requestedAt) return undefined;
        const tick = () => setRemainingMs(remainingUnlockMs(requestedAt));
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [requestedAt]);

    const handleSignOut = () => {
        auth.signOut().then(() => {
            navigate('/signup');
        }).catch((error) => {
            console.error('Error signing out:', error);
        });
    };

    const handleRequestUnlock = async () => {
        if (requestStatus === 'sending' || requestStatus === 'sent') return;
        setRequestStatus('sending');
        setRequestError('');
        try {
            const functionsInstance = getFunctions(undefined, 'europe-west1');
            const requestAccountUnlock = httpsCallable(functionsInstance, 'requestAccountUnlock');
            await requestAccountUnlock({ email: currentUser?.email || null });
            const startedAt = Date.now();
            setRequestedAt(startedAt);
            setRequestStatus('sent');
        } catch (error) {
            console.error('Error requesting account unlock:', error);
            setRequestError(error?.message || 'Something went wrong. Please try again.');
            setRequestStatus('error');
        }
    };

    // Loading state während Premium-Status geprüft wird
    if (isPremium === null) {
        return (
            <div className="loading-spinner">
                <h1>BonusClub</h1>
                <div className="spinner-circle"></div>
            </div>
        );
    }

    // Wenn User Premium hat, wird automatisch weitergeleitet
    if (isPremium) {
        return null;
    }

    const greetingName = userName && userName !== 'User' ? userName.split(' ')[0] : '';
    const countdown = splitCountdown(remainingMs);

    return (
        <div className="unlock-container">
            <GenerativeBackground />
            <img className="unlock-brand-logo" src="./img/BonusClub.png" alt="BonusClub Logo" />
            <div className="bonusclub-unlock-modal">
                {requestStatus === 'sent' ? (
                    <>
                        <div className="unlock-success-check" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M5 12.5l4.2 4.2L19 7.2"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <div className="bonusclub-unlock-message">
                            <h1 className="unlock-main-text">You're all set</h1>
                            <p className="unlock-sub-text">
                                Account activation usually happens within 24 hours. We'll unlock BonusClub premium as soon as your account is reviewed.
                            </p>
                        </div>
                        <div className="unlock-timer" role="timer" aria-live="off">
                            <div className="unlock-timer-unit">
                                <span className="unlock-timer-value">{padUnit(countdown.hours)}</span>
                                <span className="unlock-timer-label">Hours</span>
                            </div>
                            <span className="unlock-timer-sep">:</span>
                            <div className="unlock-timer-unit">
                                <span className="unlock-timer-value">{padUnit(countdown.minutes)}</span>
                                <span className="unlock-timer-label">Min</span>
                            </div>
                            <span className="unlock-timer-sep">:</span>
                            <div className="unlock-timer-unit">
                                <span className="unlock-timer-value">{padUnit(countdown.seconds)}</span>
                                <span className="unlock-timer-label">Sec</span>
                            </div>
                        </div>
                        <div className="bonusclub-unlock-actions">
                            <button
                                type="button"
                                onClick={handleSignOut}
                                className="altBtn altBtn--petrol unlock-cta"
                            >
                                Sign out
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="unlock-hero">
                            {userPhoto ? (
                                <img src={userPhoto} alt="" className="unlock-avatar" />
                            ) : (
                                <div className="unlock-hero-mark">
                                    <img src="./img/salesgod_light.webp" alt="" />
                                </div>
                            )}
                        </div>
                        <div className="bonusclub-unlock-message">
                            <h1 className="unlock-main-text">
                                One last step to unlock BonusClub
                            </h1>
                            <p className="unlock-sub-text">
                                {greetingName
                                    ? `${greetingName}, your profile is ready. Ask our team to activate your account and we will unlock premium for you.`
                                    : 'Your profile is ready. Ask our team to activate your account and we will unlock premium for you.'}
                            </p>
                        </div>
                        <div className="bonusclub-unlock-actions">
                            <button
                                type="button"
                                onClick={handleRequestUnlock}
                                className="altBtn altBtn--save unlock-cta"
                                disabled={requestStatus === 'sending'}
                            >
                                {requestStatus === 'sending' ? 'Sending request…' : 'Request account activation'}
                            </button>
                            {requestStatus === 'error' && (
                                <p className="unlock-error-text">{requestError}</p>
                            )}
                            <button
                                type="button"
                                onClick={handleSignOut}
                                className="altBtn altBtn--petrol"
                            >
                                Sign out
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default UnlockPage; 